import { useState } from "react";
import { cn } from "@/lib/utils";
import { logoUrl } from "@/lib/logo-version";


type Size = "sm" | "md" | "lg" | "xl";

const sizeMap: Record<Size, { box: string; text: string; tracking: string }> = {
  sm: { box: "h-8 w-8", text: "text-base", tracking: "tracking-[0.18em]" },
  md: { box: "h-10 w-10", text: "text-lg", tracking: "tracking-[0.2em]" },
  lg: { box: "h-12 w-12", text: "text-2xl", tracking: "tracking-[0.22em]" },
  xl: { box: "h-16 w-16", text: "text-3xl", tracking: "tracking-[0.24em]" },
};

interface BrandLogoProps {
  size?: Size;
  showWordmark?: boolean;
  variant?: "auto" | "light" | "dark";
  className?: string;
  wordmarkClassName?: string;
}

/**
 * Lumvio brand mark.
 *
 * Drop your high-res logo at `/public/logo.png` (or .svg) and it will render
 * automatically. Until then a matte obsidian tile with a subtle sage glyph
 * fills the slot so layouts stay balanced.
 */
export function BrandLogo({
  size = "md",
  showWordmark = true,
  variant = "auto",
  className,
  wordmarkClassName,
}: BrandLogoProps) {
  const [imgOk, setImgOk] = useState(true);
  const s = sizeMap[size];

  const wordmarkColor =
    variant === "light"
      ? "text-white"
      : variant === "dark"
      ? "text-foreground"
      : "text-foreground";

  const iconContainerClass =
    variant === "light"
      ? cn(
          "bg-white shadow-[0_0_24px_-6px_rgba(255,255,255,0.35)] ring-1 ring-white/60",
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:bg-gradient-to-br after:from-white/40 after:to-transparent"
        )
      : cn(
          "bg-[image:var(--gradient-hero)] shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
        );

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className={cn(
          "relative shrink-0 overflow-hidden rounded-xl",
          iconContainerClass,
          s.box
        )}
      >
        {imgOk ? (
          <img
            src={logoUrl()}
            alt="Lumvio — payment protection for freelancers"
            className="h-full w-full object-contain p-1"
            onError={() => setImgOk(false)}
          />
        ) : (
          // Placeholder mark until /public/logo.png is provided.
          <svg
            viewBox="0 0 32 32"
            className={cn(
              "h-full w-full p-1.5",
              variant === "light" ? "text-indigo-600" : "text-primary"
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M23 8H13a5 5 0 0 0 0 10h6a5 5 0 0 1 0 10H9" />
          </svg>
        )}
      </div>
      {showWordmark && (
        <span
          className={cn(
            "font-display font-semibold uppercase",
            s.text,
            s.tracking,
            wordmarkColor,
            wordmarkClassName
          )}
        >
          Lumvio
        </span>
      )}
    </div>
  );
}
